import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Bell, CheckCircle, Envelope } from "@phosphor-icons/react";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const open = async (n) => {
    if (!n.read) {
      await api.put(`/notifications/${n.id}/read`).catch(() => {});
      load();
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.put("/notifications/read-all").catch(() => {});
    load();
  };

  const fmt = (iso) => {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <Popover onOpenChange={(o) => o && load()}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notification-count"
              className="absolute -top-0.5 -right-0.5 h-5 min-w-[20px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-heading font-bold text-sm">Notifications</span>
          {unread > 0 && (
            <button onClick={markAll} data-testid="mark-all-read"
              className="text-xs text-primary font-medium hover:underline">Mark all read</button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              <Bell className="h-7 w-7 mx-auto opacity-40 mb-2" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : items.map((n) => (
            <button key={n.id} onClick={() => open(n)} data-testid={`notification-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-accent transition-colors flex gap-3 ${n.read ? "opacity-70" : "bg-primary/[0.03]"}`}>
              <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${n.read ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"}`}>
                {n.read ? <CheckCircle weight="duotone" className="h-4 w-4" /> : <Envelope weight="duotone" className="h-4 w-4" />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{n.title}</p>
                <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{fmt(n.created_at)}</p>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
